var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
import { attr, observable } from "@microsoft/fast-element";
import { FoundationElement } from "@microsoft/fast-foundation";
import { getResources } from "../../../../getResources";
import { hideSettings, isConfigurationSupported, onSettingsInteracted, onWidgetRemoved, onWidgetRemovedFromRiver, showSettings, updateSize } from "../../../../webUXApi/widgetApi";
import { getWidgetOrchestrator } from "../../../../widgetRuntime/orchestrator/orchestrator";
import { notifyWidgetProvider } from "../../../AdaptiveCardWidget/AdaptiveCardRenderingAndActions";
import { AddIcon, BulletIcon, CustomizeIcon, HideIcon, PinIcon, RemoveIcon, UnpinIcon } from "./menu-icons";
import { isFeatureEnabled } from "../../../../externalApi/VelocityApi";
import { widgetsBoardId } from "../../../../widgetRuntime/widgetApis/FeedModelApi";
/* eslint-disable sort-keys */
export class TitleBar extends FoundationElement {
    constructor() {
        super();
        this.resources = getResources();
        this.isActionMenuOpen = false;
        this.isMenuButtonDisabled = false;
        this.loading = false;
        this.error = false;
        this.isPinned = false;
        this.showPinIcon = false;
        this.menuItems = [];
        // Boolean for tracking when we temporarily enter medium size for settings support
        this.isTempMediumSizeRef = false;
        this.isSettingsSupported = false;
        this.widgetModelApi = null;
        this.signalApi = null;
        this.telemetryApi = null;
        this.widgetModelEventCallback = (eventName, widgetInstance) => {
            if (this.widgetInstance.widgetId === widgetInstance[0]) {
                if (eventName === "widgetAdded") {
                    this.isPinned = true;
                }
                else if (eventName === "widgetRemoved") {
                    this.isPinned = false;
                }
            }
        };
        this.closeActionMenu = () => {
            this.toggleActionMenu(false, null);
        };
        this.onOrchestratorEvent = (name) => {
            if (name === "widgetInnerFocus") {
                this.closeActionMenu();
            }
        };
        this.onOutsideClick = (event) => {
            var _a, _b, _c;
            if (event.target === this.parentElement ||
                ((_a = this.actionsMenuButtonElement) === null || _a === void 0 ? void 0 : _a.contains((event.path || ((_b = event.composedPath) === null || _b === void 0 ? void 0 : _b.call(event)))[0])) ||
                ((_c = event.composedPath) === null || _c === void 0 ? void 0 : _c.call(event).includes(this.actionsMenuButtonElement))) {
                return;
            }
            this.closeActionMenu();
        };
        this.onKeydown = (event) => {
            if (event.code === "Escape" || event.code === "Tab") {
                this.closeActionMenu();
            }
        };
        this.orchestrator = getWidgetOrchestrator();
        const widgetModelApiFactory = window.__com_microsoft_dsh_widgetModelApiFactory;
        if (widgetModelApiFactory) {
            this.widgetModelApi = widgetModelApiFactory.tryGetApi("model", 1, 1);
            this.signalApi = widgetModelApiFactory.tryGetApi("signals", 1, 1);
            this.telemetryApi = widgetModelApiFactory.tryGetApi("telemetry", 1, 1);
        }
    }
    isPinnedChanged(oldValue, newValue) {
        if (oldValue !== newValue) {
            if (newValue) {
                this.setAttribute("style", `cursor: var(--openHandCursor)`);
            }
            else {
                this.setAttribute("style", `cursor: auto`);
            }
        }
    }
    connectedCallback() {
        super.connectedCallback();
        if (this.widgetModelApi) {
            this.widgetModelApi.addEventListener(this.widgetModelEventCallback);
        }
        this.orchestrator.then(async (orch) => {
            // if the widgetInstance is found, that means it's pinned
            const pinnedWidgetInstance = (await orch.getWidgetInstances()).find((i) => {
                return i.widgetId === this.widgetInstance.widgetId;
            });
            this.isPinned = (pinnedWidgetInstance !== undefined) && this.isWidgetPinnable();
            this.showPinIcon = !isFeatureEnabled("Feature_WidgetHeaderUpdates");
            // Add listener for focus change event from a widget
            orch.addEventListener(this.onOrchestratorEvent);
        });
        this.widgetInstance.widgetInitializationPromise().then(() => {
            const settingsApi = this.widgetInstance.tryGetApi("settings", 1, 1);
            if (settingsApi) {
                settingsApi.addEventListenerInternal(this);
                this.isSettingsSupported = settingsApi.isConfigurationSupported();
            }
            const propertiesApi = this.widgetInstance.tryGetApi("properties", 1, 1);
            if (propertiesApi) {
                this.title = propertiesApi.title;
                this.activationUrl = propertiesApi.activationUrl;
                this.widgetIconUri = propertiesApi.getProperty("widgetIconUri");
                propertiesApi.addPropertyChangeListener((p) => this.onPropertyChange(p));
            }
        });
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        if (this.widgetModelApi) {
            this.widgetModelApi.removeEventListener(this.widgetModelEventCallback);
        }
        this.orchestrator.then(async (orch) => {
            orch.removeEventListener(this.onOrchestratorEvent);
        });
    }
    onTitleClick(event) {
        if (this.widgetInstance.isPlatform) {
            notifyWidgetProvider(this.widgetInstance, {
                "eventKind": "interaction",
                "interactionKind": "titleBar",
            });
        }
        event.stopPropagation();
        return true;
    }
    onActivationUrlClick(event) {
        // Send signal for telemetry - click on activation URL
        this.sendSignalForWidgetInteraction(0 /* header */);
        if (this.telemetryApi) {
            this.telemetryApi.sendWebXTWidgetActivationUrlTelemetryAsync(this.widgetInstance);
        }
        if (this.widgetInstance.isPlatform) {
            notifyWidgetProvider(this.widgetInstance, {
                "eventKind": "interaction",
                "interactionKind": "additionalInfoUri",
                "uri": this.widgetInstance.widgetRegistration.activationUrl,
            });
        }
        event.stopPropagation();
        return true;
    }
    toggleActionMenu(isActionMenuOpen, event) {
        var _a;
        this.isActionMenuOpen = isActionMenuOpen;
        // enable event listener if menu open and disable if menu close
        if (isActionMenuOpen) {
            // Send signal for telemetry - click on context menu
            this.sendSignalForWidgetInteraction(2 /* moreOptions */);
            onSettingsInteracted(this.widgetInstance);
            this.actionsMenuButtonElement = event && event.currentTarget;
            this.actionsMenuButtonBoundingRect = (_a = this.actionsMenuButtonElement) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect();
            this.menuItems = this.getActionMenuItems();
            document.addEventListener("click", this.onOutsideClick);
            document.addEventListener("keydown", this.onKeydown);
            // Traditional scroll and hidden events are not dispatched since feed is in a div rather than a window/document so we must use webXT's events
            window.addEventListener("dashboardscroll", this.closeActionMenu, { passive: true });
            window.addEventListener("dashboardhidden", this.closeActionMenu, { passive: true });
        }
        else {
            this.actionsMenuButtonElement = undefined;
            document.removeEventListener("click", this.onOutsideClick);
            document.removeEventListener("keydown", this.onKeydown);
            window.removeEventListener("dashboardscroll", this.closeActionMenu);
            window.removeEventListener("dashboardhidden", this.closeActionMenu);
        }
        // Send WebXT telemetry if the toggle is from user click on [...]
        if (event) {
            // Telemetry event values provided to us by WebXT for a context menu click scenario
            if (this.telemetryApi) {
                this.telemetryApi.sendWebXTWidgetActionMenuTelemetryAsync(this.widgetInstance, isActionMenuOpen);
            }
            event.stopPropagation();
        }
    }
    widgetSettingsEventsCallback(eventName) {
        if (eventName === "onShowSettings") {
            this.isMenuButtonDisabled = true;
            if (this.widgetInstance.getWidgetProperty("size") === "small") {
                this.isTempMediumSizeRef = true;
                this.updateSize("medium");
            }
            showSettings(this.widgetInstance);
        }
        else if (eventName === "onHideSettings" && this.isMenuButtonDisabled) {
            hideSettings(this.widgetInstance);
            if (this.isTempMediumSizeRef) {
                this.updateSize("small");
                this.isTempMediumSizeRef = false;
            }
            this.isMenuButtonDisabled = false;
        }
        else if (eventName === "listenerChanged") {
            isConfigurationSupported(this.widgetInstance)
                .then((showSettings) => (this.isSettingsSupported = showSettings));
        }
    }
    sendSignalForWidgetInteraction(clickType) {
        if (this.signalApi) {
            let payload = {
                widgetItem: {
                    appId: this.widgetInstance.widgetRegistration.appId,
                    isPeregrine: false,
                    isPlatform: this.widgetInstance.isPlatform,
                    providerId: this.widgetInstance.widgetRegistration.providerId,
                    widgetDefinitionId: this.widgetInstance.widgetDefinitionId,
                    widgetInstanceId: this.widgetInstance.widgetId,
                },
                clickAction: { clickType: clickType },
            };
            let signal = { name: "OnWidgetClick", payload: JSON.stringify(payload), signalType: "WDX" };
            this.signalApi.sendSignal(signal);
        }
    }
    sendSignalForSettingInteraction(change, oldSize, newSize) {
        if (this.signalApi) {
            let payload = {
                widgetItem: {
                    widgetDefinitionId: this.widgetInstance.widgetDefinitionId,
                    widgetInstanceId: this.widgetInstance.widgetId,
                    providerId: this.widgetInstance.widgetRegistration.providerId,
                    appId: this.widgetInstance.widgetRegistration.appId,
                    isPlatform: this.widgetInstance.isPlatform,
                    isPinned: this.isPinned,
                    isPeregrine: false,
                    widgetState: this.widgetInstance.widgetState,
                },
                settingsAction: {
                    settingsType: change,
                    oldSize: oldSize,
                    newSize: newSize,
                },
            };
            let signal = { name: "OnSettingsInteracted", payload: JSON.stringify(payload), signalType: "WDX" };
            this.signalApi.sendSignal(signal);
        }
    }
    updateSize(size) {
        if (size !== this.widgetInstance.getWidgetProperty("size")) {
            updateSize(this.widgetInstance, size, "onSizeChangeStart");
            updateSize(this.widgetInstance, size, "onSizeChangeEnd");
        }
    }
    onPropertyChange(property) {
        const propertiesApi = this.widgetInstance.tryGetApi("properties", 1, 1);
        if (property === "activationUrl" && propertiesApi) {
            this.activationUrl = propertiesApi.activationUrl;
        }
        else if (property === "title" && propertiesApi) {
            this.title = propertiesApi.title;
        }
        else if (property === "widgetIconUri" && propertiesApi) {
            this.widgetIconUri = propertiesApi.getProperty("widgetIconUri");
        }
    }
    onSizeClick(newSize) {
        let currSize = this.widgetInstance.getWidgetProperty("size");
        if (newSize !== currSize) {
            this.sendSignalForSettingInteraction(0 /* Resize */, currSize, newSize);
            this.updateSize(newSize);
            if (this.telemetryApi) {
                this.telemetryApi.sendWebXTWidgetSizeChangeTelemetryAsync(this.widgetInstance, newSize);
            }
            if (this.widgetInstance.isPlatform) {
                notifyWidgetProvider(this.widgetInstance, {
                    "eventKind": "interaction",
                    "interactionKind": "contextMenu",
                    "clickedItem": newSize,
                });
            }
        }
        this.closeActionMenu();
    }
    /**
     * Get widget action menu per config
     */
    getActionMenuItems() {
        const menuItems = [];
        if (this.isPinned) {
            if (this.widgetInstance.widgetRegistration.supportedSizes.length > 1) {
                if (this.widgetInstance.widgetRegistration.supportedSizes.includes("small")) {
                    menuItems.push({
                        ariaLabel: this.widgetInstance.getWidgetProperty("size") === "small" ? this.resources.WidgetContextMenu_size_small_Selected : this.resources.WidgetContextMenu_size_small_NonSelected,
                        glyph: this.widgetInstance.getWidgetProperty("size") === "small" ? BulletIcon : undefined,
                        title: this.resources.WidgetContextMenu_size_small_MenuItem,
                        id: "small",
                        onClick: () => {
                            this.onSizeClick("small");
                        },
                    });
                }
                if (this.widgetInstance.widgetRegistration.supportedSizes.includes("medium")) {
                    menuItems.push({
                        ariaLabel: this.widgetInstance.getWidgetProperty("size") === "medium" ? this.resources.WidgetContextMenu_size_medium_Selected : this.resources.WidgetContextMenu_size_medium_NonSelected,
                        glyph: this.widgetInstance.getWidgetProperty("size") === "medium" ? BulletIcon : undefined,
                        title: this.resources.WidgetContextMenu_size_medium_MenuItem,
                        id: "medium",
                        onClick: () => {
                            this.onSizeClick("medium");
                        },
                    });
                }
                if (this.widgetInstance.widgetRegistration.supportedSizes.includes("large")) {
                    menuItems.push({
                        ariaLabel: this.widgetInstance.getWidgetProperty("size") === "large" ? this.resources.WidgetContextMenu_size_large_Selected : this.resources.WidgetContextMenu_size_large_NonSelected,
                        glyph: this.widgetInstance.getWidgetProperty("size") === "large" ? BulletIcon : undefined,
                        title: this.resources.WidgetContextMenu_size_large_MenuItem,
                        id: "large",
                        onClick: () => {
                            this.onSizeClick("large");
                        },
                    });
                }
                menuItems.push({
                    isDivider: true,
                });
            }
            if (this.isSettingsSupported) {
                menuItems.push({
                    glyph: CustomizeIcon,
                    title: this.resources.FeedContextMenu_Customize,
                    id: "customize",
                    onClick: () => {
                        this.sendSignalForSettingInteraction(1 /* Edit */);
                        if (this.widgetInstance.isPlatform) {
                            notifyWidgetProvider(this.widgetInstance, {
                                "eventKind": "interaction",
                                "interactionKind": "contextMenu",
                                "clickedItem": "customize",
                            });
                        }
                        const settingsApi = this.widgetInstance.tryGetApi("settings", 1, 1);
                        if (settingsApi) {
                            settingsApi.showSettings();
                        }
                        this.closeActionMenu();
                    },
                });
            }
            menuItems.push({
                glyph: isFeatureEnabled("Feature_WidgetHeaderUpdates") ? RemoveIcon : UnpinIcon,
                title: isFeatureEnabled("Feature_WidgetHeaderUpdates")
                    ? this.resources.FeedContextMenu_Remove
                    : this.resources.FeedContextMenu_Unpin,
                id: "unpin",
                onClick: () => {
                    this.sendSignalForSettingInteraction(2 /* Unpin */);
                    onWidgetRemoved(this.widgetInstance);
                    this.isPinned = false;
                    this.closeActionMenu();
                    if (this.telemetryApi) {
                        this.telemetryApi.sendWebXTWidgetUnpinTelemetryAsync(this.widgetInstance);
                    }
                    if (this.widgetInstance.isPlatform) {
                        notifyWidgetProvider(this.widgetInstance, {
                            "eventKind": "interaction",
                            "interactionKind": "contextMenu",
                            "clickedItem": "unpin",
                        });
                    }
                },
            });
        }
        else {
            if (this.isWidgetPinnable()) {
                menuItems.push({
                    glyph: HideIcon,
                    title: this.resources.FeedContextMenu_Hide,
                    id: "hide",
                    onClick: () => {
                        this.sendSignalForSettingInteraction(3 /* Hide */);
                        if (this.widgetInstance.isPlatform) {
                            notifyWidgetProvider(this.widgetInstance, {
                                "eventKind": "interaction",
                                "interactionKind": "contextMenu",
                                "clickedItem": "hide",
                            });
                        }
                        onWidgetRemovedFromRiver(this.widgetInstance, true /*hide*/);
                        this.closeActionMenu();
                    },
                });
            }
            if (this.isSettingsSupported) {
                menuItems.push({
                    glyph: CustomizeIcon,
                    title: this.resources.FeedContextMenu_Customize,
                    id: "customize",
                    onClick: () => {
                        this.sendSignalForSettingInteraction(1 /* Edit */);
                        if (this.widgetInstance.isPlatform) {
                            notifyWidgetProvider(this.widgetInstance, {
                                "eventKind": "interaction",
                                "interactionKind": "contextMenu",
                                "clickedItem": "customize",
                            });
                        }
                        const settingsApi = this.widgetInstance.tryGetApi("settings", 1, 1);
                        if (settingsApi) {
                            settingsApi.showSettings();
                        }
                        this.closeActionMenu();
                    },
                });
            }
            if (this.isWidgetPinnable()) {
                menuItems.push({
                    glyph: isFeatureEnabled("Feature_WidgetHeaderUpdates") ? AddIcon : PinIcon,
                    title: isFeatureEnabled("Feature_WidgetHeaderUpdates")
                        ? this.resources.FeedContextMenu_Add
                        : this.resources.FeedContextMenu_Pin,
                    id: "pin",
                    onClick: () => {
                        this.sendSignalForSettingInteraction(4 /* Pin */);
                        if (this.widgetInstance.isPlatform) {
                            notifyWidgetProvider(this.widgetInstance, {
                                "eventKind": "interaction",
                                "interactionKind": "contextMenu",
                                "clickedItem": "pin",
                            });
                        }
                        this.orchestrator.then(async (orch) => orch.addWidgetInstance(this.widgetInstance.widgetRegistration, this.widgetInstance.widgetId));
                        this.isPinned = true;
                        this.closeActionMenu();
                    },
                });
            }
        }
        const propertiesApi = this.widgetInstance.tryGetApi("properties", 1, 1);
        if (propertiesApi === null || propertiesApi === void 0 ? void 0 : propertiesApi.attribution) {
            menuItems.push({
                isDivider: true,
            });
            menuItems.push({
                title: propertiesApi === null || propertiesApi === void 0 ? void 0 : propertiesApi.attribution,
                ariaLabel: propertiesApi === null || propertiesApi === void 0 ? void 0 : propertiesApi.attribution,
                id: "attribution",
                isText: true,
                onClick: () => {
                    if (this.widgetInstance.isPlatform) {
                        notifyWidgetProvider(this.widgetInstance, {
                            "eventKind": "interaction",
                            "interactionKind": "contextMenu",
                            "clickedItem": "attribution",
                        });
                    }
                }
            });
        }
        return menuItems;
    }
    // Windows must wait for signal from WebXT to render 3P + Outlook widgets
    // In Boards Lite, Widgets are only pinnable on Widgets-Only board
    // Widgets-Only Board: Widgets can be pinned/unpinned via context menu
    // All Other Boards: Widgets can be shown, but option to pin/unpin is disabled
    isWidgetPinnable() {
        if (isFeatureEnabled("Feature_BoardsLite")) {
            if (this.widgetInstance.boardView) {
                return this.widgetInstance.boardView === widgetsBoardId;
            }
        }
        return true;
    }
}
__decorate([
    attr,
    __metadata("design:type", Object)
], TitleBar.prototype, "resources", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], TitleBar.prototype, "widgetInstance", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], TitleBar.prototype, "isActionMenuOpen", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], TitleBar.prototype, "isMenuButtonDisabled", void 0);
__decorate([
    observable,
    __metadata("design:type", String)
], TitleBar.prototype, "title", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], TitleBar.prototype, "activationUrl", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], TitleBar.prototype, "widgetIconUri", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], TitleBar.prototype, "loading", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], TitleBar.prototype, "error", void 0);
__decorate([
    observable,
    __metadata("design:type", String)
], TitleBar.prototype, "lastUpdated", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], TitleBar.prototype, "isPinned", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], TitleBar.prototype, "showPinIcon", void 0);
__decorate([
    observable,
    __metadata("design:type", Array)
], TitleBar.prototype, "menuItems", void 0);
